import { forwardRef, useEffect, useMemo, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import { useTranslation } from "react-i18next";
import {
  Reminder as ReminderType,
  ReminderTimePeriod
} from "@/entities/tasks/types";
import IconContainer from "@/components/ui/icon-container";
import { Add, BellRing } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import { findIndex, isNull, map, uniqueId } from "lodash";
import Reminder from "@/components/controls/RemindersField/Reminder";
import { displaySelectedReminders } from "@/entities/tasks/helpers";
import SingleCheckbox from "@/components/ui/SingleCheckbox";

export interface RemindersFieldProps {
  value: ReminderType[] | null;
  onChange: (value: ReminderType[] | null) => void;
  onUpdateOnlyNotifyAssignee: (value: boolean) => void;
  name: string;
  placeholder?: string;
  showOnlyNotifyAssignee?: boolean;
  shouldOnlyNotifyAssignee?: boolean;
  className?: string;
  disabled?: boolean;
  disabledTooltip?: string;
}

const TRANSLATION_KEY = "tasks.createModal.remindersPopover";

const createDefaultReminder = (): ReminderType => ({
  id: uniqueId("reminder-"),
  value: 1,
  timePeriod: ReminderTimePeriod.DAYS,
  customDate: null
});

const RemindersField = forwardRef<HTMLInputElement, RemindersFieldProps>(
  (
    {
      value,
      onChange,
      onUpdateOnlyNotifyAssignee,
      name,
      placeholder,
      showOnlyNotifyAssignee = false,
      shouldOnlyNotifyAssignee = false,
      className,
      disabled = false,
      disabledTooltip
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [remindersState, setRemindersState] = useState<ReminderType[]>([
      createDefaultReminder()
    ]);
    const [localShouldOnlyNotifyAssignee, setLocalShouldOnlyNotifyAssignee] =
      useState(shouldOnlyNotifyAssignee);

    const validReminders = useMemo(
      () => remindersState.filter((reminder) => reminder.value !== 0),
      [remindersState]
    );

    useEffect(() => {
      if (value && !isNull(value) && value.length > 0) {
        setRemindersState([...value]);
      } else {
        setRemindersState([createDefaultReminder()]);
      }
    }, [isOpen]);

    useEffect(() => {
      if (isOpen) {
        setLocalShouldOnlyNotifyAssignee(shouldOnlyNotifyAssignee);
      }
    }, [isOpen, shouldOnlyNotifyAssignee]);

    const displayValue = useMemo(
      () =>
        value && value.length > 0 ? displaySelectedReminders(value, t) : "",
      [value]
    );

    const updateRemindersState = (
      reminderId: string,
      fieldName: keyof ReminderType,
      newValue: number | ReminderTimePeriod | string | null
    ) => {
      const reminderIndex = findIndex(
        remindersState,
        (reminder) => reminder.id === reminderId
      );
      const newRemindersState = [...remindersState];
      const updatedReminder = { ...remindersState[reminderIndex] };
      (updatedReminder[fieldName] as any) = newValue;
      newRemindersState[reminderIndex] = updatedReminder;
      setRemindersState(newRemindersState);
    };

    const handleAddReminder = () => {
      const newRemindersState = [...remindersState, createDefaultReminder()];
      setRemindersState(newRemindersState);
    };

    const handleDeleteReminder = (reminderId: string) => {
      const newRemindersState = [...remindersState];
      const reminderIndex = findIndex(
        remindersState,
        (reminder) => reminder.id === reminderId
      );
      newRemindersState.splice(reminderIndex, 1);
      setRemindersState(newRemindersState);
    };

    const triggerContent = (
      <PopoverTrigger asChild disabled={disabled}>
        <div className={cn("relative", !disabled && (isOpen ? "shadow-field-focus" : "shadow-field"))}>
          <div
            ref={ref}
            className={cn(
              "w-full border-[0.5px] border-border-field-default hover:border-border-field-hover rounded-lg pl-3 pr-2 py-[6px] gap-[6px] bg-surface-field-default flex items-center transition-all duration-200 ease-in-out min-h-9",
              isOpen && "border-border-field-focus bg-surface-focus-selected",
              disabled
                ? "cursor-not-allowed !bg-surface-fields-disabled !border-border-field-disabled"
                : "cursor-pointer",
              className
            )}
            data-test={`${name}-input`}
          >
            <IconContainer
              color={disabled ? "disabled" : "secondary"}
              className="flex-shrink-0"
            >
              <BellRing />
            </IconContainer>
            <Text
              as="span"
              variant={displayValue ? "body" : "bodyTertiary"}
              className={cn(disabled && "!text-disabled")}
            >
              {displayValue || placeholder}
            </Text>
          </div>
        </div>
      </PopoverTrigger>
    );

    return (
      <Popover
        modal
        open={isOpen}
        onOpenChange={(open) => !disabled && setIsOpen(open)}
      >
        {disabled && disabledTooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>{triggerContent}</TooltipTrigger>
            <TooltipContent className="max-w-44">
              {disabledTooltip}
            </TooltipContent>
          </Tooltip>
        ) : (
          triggerContent
        )}
        <PopoverContent
          className="w-[320px] flex flex-col gap-3"
          align="start"
          sideOffset={4}
        >
          <Text variant="bodySecondary">
            {t(`${TRANSLATION_KEY}.description`)}
          </Text>
          <div className="flex flex-col gap-2">
            {map(remindersState, (reminder) => (
              <Reminder
                key={reminder.id}
                reminder={reminder}
                updateRemindersState={updateRemindersState}
                handleDeleteReminder={handleDeleteReminder}
                showDeleteIcon={true}
              />
            ))}
            <Button
              variant="tertiary"
              className="w-fit h-fit flex gap-1"
              onClick={handleAddReminder}
              disabled={remindersState.length === 5}
            >
              <Add />
              <span>{t("add")}</span>
            </Button>
          </div>
          {showOnlyNotifyAssignee && (
            <SingleCheckbox
              id="onlyNotifyAssignee"
              label={t(`${TRANSLATION_KEY}.onlyNotifyAssignee`)}
              isChecked={localShouldOnlyNotifyAssignee}
              onCheckedChange={(checked: boolean) =>
                setLocalShouldOnlyNotifyAssignee(checked)
              }
            />
          )}
          <div className="flex w-full justify-end">
            <div className="flex gap-2">
              <Button onClick={() => setIsOpen(false)} variant="secondary">
                {t("cancel")}
              </Button>
              <Button
                onClick={() => {
                  onChange(validReminders.length > 0 ? validReminders : null);
                  onUpdateOnlyNotifyAssignee(localShouldOnlyNotifyAssignee);
                  setIsOpen(false);
                }}
              >
                {t("apply")}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);
RemindersField.displayName = "RemindersField";

export default RemindersField;
